/**
 * `v-deal`: a list's children arrive one after another, like cards dealt onto
 * a table (life-layer.md §2, "v-deal list stagger").
 *
 * Owns: the on-mount arrival of the directive's element's children. Must not:
 * react to later changes — a row added to a live list is Vue's business, and
 * re-dealing a list the player is reading would be motion against them.
 * Under reduced motion it does nothing.
 *
 * Defaults are the spec's: y 14, rotate -1.5deg, 0.3 s, `back.out(1.6)`,
 * stagger 40 ms. A caller may override `duration`, `stagger` and `delay`.
 */
import type { ObjectDirective } from 'vue';

import { reducedMotion } from '@/engine/motion';

import { gsap } from './gsap';

export interface DealOptions {
  duration?: number;
  stagger?: number;
  delay?: number;
}

export const vDeal: ObjectDirective<HTMLElement, DealOptions | undefined> = {
  mounted(el, binding) {
    if (reducedMotion() || el.children.length === 0) return;
    const { duration = 0.3, stagger = 0.04, delay = 0 } = binding.value ?? {};
    gsap.from(Array.from(el.children), {
      y: 14,
      rotation: -1.5,
      opacity: 0,
      duration,
      stagger,
      delay,
      ease: 'back.out(1.6)',
      clearProps: 'opacity,transform',
    });
  },
  beforeUnmount(el) {
    gsap.killTweensOf(Array.from(el.children));
  },
};
